import './Outdated.css';
import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  collection,
  query,
  onSnapshot,
  doc,
  setDoc,
} from 'firebase/firestore';
import { AuthContext } from '../../../services/AuthContext';

const ENTRIES_COLLECTION = 'pdfEntries';


function Vencimientos() {
  const navigate = useNavigate();
  const { user, firebaseUid, db } = useContext(AuthContext);

  const [allEntries, setAllEntries] = useState([]);
  const [authorizedUids, setAuthorizedUids] = useState([]);
  const [loadingAuth, setLoadingAuth] = useState(true);
  const [editingRowId, setEditingRowId] = useState(null);

  const [activeSubTab, setActiveSubTab] = useState('vencidos');

  useEffect(() => {
    if (!db) return;
    const q = query(collection(db, 'userRoles'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const uids = [];
      snapshot.forEach((doc) => {
        if (doc.data().canEditCNA === true) uids.push(doc.id);
      });
      setAuthorizedUids(uids);
      setLoadingAuth(false);
    });
    return unsubscribe;
  }, [db]);

  useEffect(() => {
    if (!db || loadingAuth) return;
    const q = query(collection(db, ENTRIES_COLLECTION));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const list = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      setAllEntries(list);
    });
    return unsubscribe;
  }, [db, loadingAuth]);

  const isAuthorized = firebaseUid && authorizedUids.includes(firebaseUid);
  const isRowEditable = (id) => isAuthorized && editingRowId === id;

  const parseDate = (vencimiento) => {
    if (!vencimiento || typeof vencimiento !== 'string') return null;
    const str = vencimiento.trim();
    const parts = str.split(/[/\.-]/);
    if (parts.length !== 3) return null;

    let day, month, year;
    if (parts[0].length <= 2 && parts[1].length <= 2 && parts[2].length === 4) {
      day = parseInt(parts[0], 10);
      month = parseInt(parts[1], 10) - 1;
      year = parseInt(parts[2], 10);
    } else if (parts[0].length === 4 && parts[2].length <= 2) {
      year = parseInt(parts[0], 10);
      month = parseInt(parts[1], 10) - 1;
      day = parseInt(parts[2], 10);
    } else return null;

    const date = new Date(year, month, day);
    if (isNaN(date.getTime())) return null;
    date.setHours(0, 0, 0, 0);
    return date;
  };

  const today = new Date(); today.setHours(0, 0, 0, 0);
  const treintaDias = new Date(today); treintaDias.setDate(today.getDate() + 45);

  const vencidos = allEntries.filter((e) => {
    const fecha = parseDate(e.vencimiento);
    return fecha && fecha < today;
  });

  const proximos = allEntries.filter((e) => {
    const fecha = parseDate(e.vencimiento);
    return fecha && fecha >= today && fecha <= treintaDias;
  });

  const enOrden = allEntries.filter((e) => {
    const fecha = parseDate(e.vencimiento);
    const esCumplido = e.estadoGestion === 'Cumple' && e.auditoria2025 === 'Cumple';
    return fecha && fecha > treintaDias && esCumplido;
  });

  const filteredEntries =
    activeSubTab === 'vencidos' ? vencidos :
    activeSubTab === 'proximos' ? proximos : enOrden;

  const handleUpdate = async (id, field, value) => {
    if (!isAuthorized || !db) return;
    await setDoc(doc(db, ENTRIES_COLLECTION, id), { [field]: value }, { merge: true });
  };

  const handleDownload = (url, name) => {
    if (!url) return;
    const a = document.createElement('a');
    a.href = url;
    a.download = name || 'archivo.pdf';
    a.click();
  };

  useEffect(() => {
  document.querySelectorAll('.PdfTable textarea').forEach((ta) => {
    ta.style.height = 'auto';
    ta.style.height = ta.scrollHeight + 'px';   
  });}, [allEntries, editingRowId, activeSubTab]);

  const autoResize = (e) => {
    const el = e.target;
    
    if (!isRowEditable(el.closest('tr')?.getAttribute('data-id'))) return;

    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  };

  if (loadingAuth) return <div>Cargando permisos...</div>;

  return (
    <div className="Vencimientos">
      <h2>Vencimientos</h2>

      <div className="vencimientos-subtabs">
        <button
          className={`subtab-button subtab-vencidos ${activeSubTab === 'vencidos' ? 'active' : ''}`}
          onClick={() => setActiveSubTab('vencidos')}
        >
          Documentos vencidos ({vencidos.length})
        </button>
        <button
          className={`subtab-button subtab-proximos ${activeSubTab === 'proximos' ? 'active' : ''}`}
          onClick={() => setActiveSubTab('proximos')}
        >
          Próximos a vencer ({proximos.length})
        </button>
        <button
          className={`subtab-button subtab-enorden ${activeSubTab === 'enorden' ? 'active' : ''}`}
          onClick={() => setActiveSubTab('enorden')}
        >
          Documentos en orden ({enOrden.length})
        </button>
      </div>

      <table className="PdfTable">
        <thead>
          <tr>
            <th style={{ width: '5rem' }}>NORMA</th>
            <th style={{ width: '12rem' }}>ACCIÓN DE CUMPLIMIENTO</th>
            <th style={{ width: '12rem' }}>ESTADO</th>
            <th style={{ width: '6rem' }}>VENCIMIENTO</th>
            <th style={{ width: '10rem' }}>OBSERVACIONES</th>
            <th style={{ width: '10rem' }}>HALLAZGO</th>
            <th style={{ width: '12rem' }}>AUDITORÍA</th>
            <th style={{ width: '5rem', height: '100%' }}>ACCIONES</th>
          </tr>
        </thead>
        <tbody>
          {filteredEntries.map((entry) => {
            const editable = isRowEditable(entry.id);

            return (
              <tr key={entry.id} className={editable ? 'editing' : ''} data-id={entry.id}>
                <td>
                  <span
                    onClick={() => handleDownload(entry.downloadURL, entry.norma)}
                    style={{ cursor: 'pointer', textDecoration: 'underline' }}
                  >
                    {entry.norma || entry.fileName}
                  </span>
                </td>

                <td>
                  <textarea
                    value={entry.accionCumplimiento || ''}
                    readOnly={!editable}
                    onChange={(e) => {
                      handleUpdate(entry.id, 'accionCumplimiento', e.target.value);
                      autoResize(e);
                    }}
                    onInput={autoResize}
                  />
                </td>

                <td>
                  <select
                    value={entry.estadoGestion || 'Informativo'}
                    onChange={(e) => handleUpdate(entry.id, 'estadoGestion', e.target.value)}
                    disabled={!editable}
                  >
                    <option value="No cumple">No cumple</option>
                    <option value="En trámite C/Pendiente">En trámite C/Pendiente</option>
                    <option value="En trámite S/Pendiente">En trámite S/Pendiente</option>
                    <option value="Se debe adecuar">Se debe adecuar</option>
                    <option value="Cumple">Cumple</option>
                    <option value="Informativo">Informativo</option>
                  </select>
                </td>

                <td>
                  <input
                    type="text"
                    value={entry.vencimiento || ''}
                    readOnly={!editable}
                    onChange={(e) => handleUpdate(entry.id, 'vencimiento', e.target.value)}
                  />
                </td>

                <td>
                  <textarea
                    value={entry.observaciones || ''}
                    readOnly={!editable}
                    onChange={(e) => {
                      handleUpdate(entry.id, 'observaciones', e.target.value);
                      autoResize(e);
                    }}
                    onInput={autoResize}
                  />
                </td>

                <td>
                  <textarea
                    value={entry.hallazgoAuditoria || ''}
                    readOnly={!editable}
                    onChange={(e) => {
                      handleUpdate(entry.id, 'hallazgoAuditoria', e.target.value);
                      autoResize(e);
                    }}
                    onInput={autoResize}
                  />
                </td>

                <td>
                  <select
                    value={entry.auditoria2025 || 'Informativo'}
                    onChange={(e) => handleUpdate(entry.id, 'auditoria2025', e.target.value)}
                    disabled={!editable}
                  >
                    <option value="No cumple">No cumple</option>
                    <option value="En trámite C/Pendiente">En trámite C/Pendiente</option>
                    <option value="En trámite S/Pendiente">En trámite S/Pendiente</option>
                    <option value="Se debe adecuar">Se debe adecuar</option>
                    <option value="Cumple">Cumple</option>
                    <option value="Informativo">Informativo</option>
                  </select>
                </td>

                <td style={{ display: 'flex', flexDirection: 'column', gap: '8px', border: '0px;'}}>
                  {isAuthorized && (
                    <button
                      onClick={() =>
                        setEditingRowId(editingRowId === entry.id ? null : entry.id)
                      }
                    >
                      {editable ? 'Bloquear' : 'Editar'}
                    </button>
                  )}

                  {entry.folder && (
                    <button
                      onClick={() => navigate(`/${entry.folder}`)}
                      className="go-to-category-btn"
                      title="Ir a la categoría"
                    >
                      →
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {filteredEntries.length === 0 && <p>No hay documentos en esta sección.</p>}
    </div>
  );
}

export default Vencimientos;
